import React, { useState, useEffect } from 'react';
import { PlusCircle, List, LayoutGrid, ClipboardList, Loader2, CheckCircle2, Archive, AlertTriangle, FileText } from 'lucide-react';
import OverviewCards from '../../components/common/OverviewCards';
import TaskTable from '../../components/Tasks/TaskTable';
import TaskGrid from '../../components/Tasks/TaskGrid';
import ReportsTable from '../../components/reports/ReportsTable';
import CreateTaskModal from '../../components/Tasks/CreateTaskModal';
import api from '../../utils/axios';
import axios from 'axios';
import { Toaster, toast } from 'react-hot-toast';

const TaskManagementPage = () => {
  const [tasks, setTasks] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('table'); // 'table' or 'grid'
  const [activeTab, setActiveTab] = useState('tasks'); // 'tasks' or 'reports'
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);

  const fetchTasks = async () => {
    try {
      const response = await api.get('/api/tasks');
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to fetch tasks.');
    }
  };

  const fetchReports = async () => {
    try {
      const response = await api.get('/api/reports');
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      // If unauthorized, redirect to login
      if (error.response && error.response.status === 401) {
        // You might want to redirect to login or show a login modal
        console.log('User not authenticated, redirecting to login...');
        // window.location.href = '/login'; // Uncomment if you want to redirect to login
      }
      toast.error('Failed to fetch reports. ' + (error.response?.data?.message || ''));
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchTasks(), fetchReports()]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const overviewData = [
    {
      title: 'Total Tasks',
      value: tasks.length,
      icon: <ClipboardList size={24} className="text-blue-500" />,
      color: 'bg-blue-100',
    },
    {
      title: 'Pending Reports',
      value: reports.filter(r => r.status === 'pending').length,
      icon: <AlertTriangle size={24} className="text-red-500" />,
      color: 'bg-red-100',
    },
    {
      title: 'In Progress',
      value: tasks.filter(t => t.status === 'inProgress').length + reports.filter(r => r.status === 'in_progress').length,
      icon: <Loader2 size={24} className="text-orange-500" />,
      color: 'bg-orange-100',
    },
    {
      title: 'Completed',
      value: tasks.filter(t => t.status === 'completed').length + reports.filter(r => r.status === 'resolved').length,
      icon: <CheckCircle2 size={24} className="text-green-500" />,
      color: 'bg-green-100',
    },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Toaster position="top-center" reverseOrder={false} />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Task & Report Management</h1>
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setCreateModalOpen(true)} 
            className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            <PlusCircle size={20} />
            Create Task
          </button>
          <div className="flex items-center gap-2 bg-gray-200 p-1 rounded-lg">
            <button onClick={() => setView('table')} className={`p-2 rounded-md ${view === 'table' ? 'bg-white shadow' : ''}`}>
              <List size={20} />
            </button>
            <button onClick={() => setView('grid')} className={`p-2 rounded-md ${view === 'grid' ? 'bg-white shadow' : ''}`}>
              <LayoutGrid size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs for Tasks and Reports */}
      <div className="flex mb-6 bg-white rounded-lg p-1 shadow-sm">
        <button
          onClick={() => setActiveTab('tasks')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
            activeTab === 'tasks' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <ClipboardList size={20} />
          Tasks ({tasks.length})
        </button>
        <button
          onClick={() => setActiveTab('reports')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
            activeTab === 'reports' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <FileText size={20} />
          User Reports ({reports.length})
        </button>
      </div>

      <OverviewCards stats={overviewData} />

      <div className="mt-6 bg-white p-4 rounded-lg shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-2">Loading {activeTab}...</span>
          </div>
        ) : activeTab === 'tasks' ? (
          view === 'table' ? (
            <TaskTable tasks={tasks} onRefresh={fetchAllData} />
          ) : (
            <TaskGrid tasks={tasks} onRefresh={fetchAllData} />
          )
        ) : (
          <ReportsTable reports={reports} onRefresh={fetchAllData} />
        )}
      </div>

      {isCreateModalOpen && (
        <CreateTaskModal 
          isOpen={isCreateModalOpen} 
          onClose={() => setCreateModalOpen(false)} 
          onTaskCreated={fetchAllData} 
        />
      )}
    </div>
  );
};

export default TaskManagementPage;
